import { useState, type SyntheticEvent, type JSX } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { ApiError, authApi } from '@invite/app-core';
import { AuthLayout } from '../app/AuthLayout';
import { useT } from '../i18n/useT';
import { twoFactorSession } from './twoFactorSession';
import { useCompleteLogin } from './useLoginFlow';

export const TwoFactorVerifyScreen = (): JSX.Element => {
  const t = useT();
  const navigate = useNavigate();
  const completeLogin = useCompleteLogin();
  // Captured once: a later clear() (on success) must not bounce this screen back to login.
  const [session] = useState(() => twoFactorSession.get());
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(session?.infoMessage ?? null);
  // One request at a time: a second tap would spend the one-time code twice.
  const [busy, setBusy] = useState(false);

  if (session === null) {
    return <Navigate to="/login" replace />;
  }
  const config = session.config;
  const isConfigFlow = config !== undefined;
  const isSms = (config?.isSmsFallback === true ? 'sms' : session.verificationType) === 'sms';
  const phone = config?.phone ?? session.phone ?? '';

  const onVerify = (event: SyntheticEvent): void => {
    event.preventDefault();
    if (busy) {
      return;
    }
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        const result = await authApi.verifyTwoFactor(session.token, { twoFactorToken: code });
        const recoveryCodes = config?.recoveryCodes ?? result.recoveryCodes;
        if (isConfigFlow && recoveryCodes !== undefined && recoveryCodes.length > 0) {
          // Keep the codes in session state so the recovery screen survives a refresh.
          twoFactorSession.update({
            token: result.token,
            config: { ...config, recoveryCodes },
            infoMessage: undefined,
            access: { token: result.token, tokenExpiryDate: result.tokenExpiryDate }
          });
          await navigate('/login/two-factor/recovery-codes');
          return;
        }
        await completeLogin(result.token, result.tokenExpiryDate, session.returnUrl);
      } catch (caught) {
        setError(
          caught instanceof ApiError
            ? caught.message
            : t('Unable to sign in right now. Please try again.')
        );
      } finally {
        setBusy(false);
      }
    })();
  };

  const onResend = (event: SyntheticEvent): void => {
    event.preventDefault();
    if (busy) {
      return;
    }
    setBusy(true);
    void (async () => {
      try {
        await authApi.resendTwoFactorSms(session.token);
        setInfo(t('A new verification code was sent.'));
      } catch (caught) {
        setInfo(
          caught instanceof ApiError
            ? caught.message
            : t('Unable to sign in right now. Please try again.')
        );
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <AuthLayout>
      <h1 className="ow-heading">{t('Two-factor verification')}</h1>
      <p className="ow-muted">
        {isConfigFlow
          ? t('Finish setting up two-factor authentication.')
          : t('Enter the code to continue.')}
      </p>
      {info !== null ? <p className="ow-info">{info}</p> : null}
      {isConfigFlow && config.qrUrl !== null && config.qrUrl !== undefined ? (
        <div className="ow-form">
          <p>{t('Scan this QR code with your authenticator app.')}</p>
          <img className="ow-qr" src={config.qrUrl} alt={t('Authenticator QR code')} />
          {config.secretKey !== null && config.secretKey !== undefined ? (
            <p className="ow-muted">
              {t('Manual key:')} <strong>{config.secretKey}</strong>
            </p>
          ) : null}
        </div>
      ) : isSms ? (
        <p>{t('We sent a code to {0}.', { 0: phone })}</p>
      ) : (
        <p>{t('Enter the code from your authenticator app.')}</p>
      )}
      <form className="ow-form" onSubmit={onVerify}>
        <label className="ow-label">
          {t('Verification code')}
          <input
            type="text"
            name="code"
            className="ow-field"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
            }}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
          />
        </label>
        {error !== null ? (
          <p className="ow-error" role="alert">
            {error}
          </p>
        ) : null}
        <button type="submit" className="ow-btn-primary" disabled={busy}>
          {t('Verify')}
        </button>
      </form>
      {isSms ? (
        <form onSubmit={onResend}>
          <button type="submit" className="ow-link" disabled={busy}>
            {t('Send a new code')}
          </button>
        </form>
      ) : null}
    </AuthLayout>
  );
};
